import { useState } from 'react'
import { Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { useLeads } from '../api/leads'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { LeadStatusBadge, LeadSourceBadge } from '../components/ui/Badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../components/ui/Table'
import { formatRelativeTime } from '../lib/utils'
import LeadForm from '../components/leads/LeadForm'
import LeadDetailModal from '../components/leads/LeadDetailModal'
import type { LeadStatus } from '@1130-studio/shared'

const statusFilters: { label: string; value: LeadStatus | undefined }[] = [
  { label: 'Все', value: undefined },
  { label: 'Новые', value: 'new' },
  { label: 'В работе', value: 'bd_assigned' },
  { label: 'Квалифицированные', value: 'qualified' },
  { label: 'Конвертированные', value: 'converted' },
  { label: 'Потерянные', value: 'lost' },
]

export default function LeadsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<LeadStatus | undefined>()
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)

  const { data, isLoading, error } = useLeads({
    status: statusFilter,
    search: search || undefined,
    page,
    pageSize: 20,
  })

  const leads = data?.items || []
  const totalPages = data?.totalPages || 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Заявки</h1>
          <p className="text-text-secondary mt-1">
            Управление входящими заявками
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4" />
          Новая заявка
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
          <Input
            placeholder="Поиск по имени, email, телефону..."
            className="pl-10"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {statusFilters.map((filter) => (
            <button
              key={filter.label}
              onClick={() => {
                setStatusFilter(filter.value)
                setPage(1)
              }}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                statusFilter === filter.value
                  ? 'bg-accent-lime-dim text-accent-lime'
                  : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-accent-lime border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-400">
            Ошибка загрузки заявок
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12 text-text-tertiary">
            {search || statusFilter ? 'Заявки не найдены' : 'Пока нет заявок'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Статус</TableHead>
                <TableHead>Имя</TableHead>
                <TableHead>Контакты</TableHead>
                <TableHead>Источник</TableHead>
                <TableHead>Ответственный</TableHead>
                <TableHead>Создана</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead) => (
                <TableRow
                  key={lead.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedLeadId(lead.id)}
                >
                  <TableCell>
                    <LeadStatusBadge status={lead.status} />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{lead.name}</div>
                    {lead.message && (
                      <div className="text-sm text-text-tertiary truncate max-w-xs">
                        {lead.message}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-sm">
                      {lead.email && (
                        <div className="text-text-secondary">{lead.email}</div>
                      )}
                      {lead.phone && (
                        <div className="text-text-tertiary">{lead.phone}</div>
                      )}
                      {lead.telegram && (
                        <div className="text-text-tertiary">@{lead.telegram.replace('@', '')}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <LeadSourceBadge source={lead.source} />
                  </TableCell>
                  <TableCell>
                    {lead.assignedUser ? (
                      <span className="text-text-secondary">
                        {lead.assignedUser.username}
                      </span>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-text-tertiary">
                      {formatRelativeTime(lead.createdAt)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle">
            <div className="text-sm text-text-tertiary">
              Страница {page} из {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create Lead Modal */}
      {showForm && (
        <LeadForm
          onClose={() => setShowForm(false)}
          onSuccess={() => setShowForm(false)}
        />
      )}

      {/* Lead Detail Modal */}
      {selectedLeadId && (
        <LeadDetailModal
          leadId={selectedLeadId}
          isOpen={!!selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
        />
      )}
    </div>
  )
}
